import { Link } from '../components/Link.tsx';
import { Hero } from '../components/marketing/Hero.tsx';
import { MarketingFooter } from '../components/marketing/MarketingFooter.tsx';
import { useDocumentTitle } from '../hooks/useDocumentTitle.ts';
import { useLocale } from '../hooks/useLocale.tsx';
import { useProPlanOptions } from '../hooks/useProPlanOptions.ts';
import { useT, type TFunction } from '../i18n.ts';
import { formatUnitAmount, yearlyMonthlyEquivalent } from '../lib/pro-plan-pricing.ts';
import { PRICING_TIERS, PRICING_FEATURES, type PricingCell, type TierPrice } from './pricing-tiers.ts';
import '../styles/marketing.css';

function renderCell(cell: PricingCell, t: TFunction) {
  if (cell.kind === 'included') {
    return <span className="mkt-pricing-yes" aria-label={t('pricing.comparison.cell.included-aria')}>✓</span>;
  }
  if (cell.kind === 'excluded') {
    return <span className="mkt-pricing-no" aria-label={t('pricing.comparison.cell.not-included-aria')}>—</span>;
  }
  return <span>{t(cell.valueKey)}</span>;
}

// Renders a tier card's price block. `static` (Free) needs no network data.
// `live` (Pro) reads useProPlanOptions()'s state: a reserved-height
// placeholder while loading (avoids layout shift once the price arrives),
// the formatted monthly price plus a yearly-billing equivalent once loaded,
// or a graceful fallback sentence if the fetch fails.
function TierPriceBlock({ price, plans, locale, t }: {
  price: TierPrice;
  plans: ReturnType<typeof useProPlanOptions>;
  locale: string;
  t: TFunction;
}) {
  if (price.kind === 'static') {
    return (
      <div className="mkt-pricing-tier-price">
        <span className="mkt-pricing-tier-price-amount">{t(price.amountKey)}</span>
      </div>
    );
  }

  if (plans.status === 'loading') {
    return <div className="mkt-pricing-tier-price" aria-live="polite" aria-busy="true" />;
  }

  if (plans.status === 'error') {
    return (
      <div className="mkt-pricing-tier-price">
        <span className="mkt-pricing-tier-price-unavailable">{t(price.unavailableKey)}</span>
      </div>
    );
  }

  const { monthly, yearly } = plans.data;
  const monthlyAmount = formatUnitAmount(monthly.unitAmount, monthly.currency, locale);
  const yearlyEquivalent = formatUnitAmount(yearlyMonthlyEquivalent(yearly.unitAmount), yearly.currency, locale);

  return (
    <div className="mkt-pricing-tier-price">
      <span className="mkt-pricing-tier-price-amount">{monthlyAmount}</span>
      <span className="mkt-pricing-tier-price-unit">{t(price.unitKey)}</span>
      <span className="mkt-pricing-tier-price-yearly">
        {t('pricing.tier.pro.price-yearly-note', { price: yearlyEquivalent })}
      </span>
    </div>
  );
}

export function Pricing() {
  const t = useT();
  const { locale } = useLocale();
  const plans = useProPlanOptions();
  useDocumentTitle(t('pricing.document-title'));

  return (
    <div className="mkt-page">
      <Hero
        eyebrow={t('pricing.hero.eyebrow')}
        title={t('pricing.hero.title')}
        lede={t('pricing.hero.lede')}
      >
        <Link to="/register" className="mkt-btn primary lg">
          {t('marketing.cta.signup-free')}
        </Link>
        <Link to="/dashboard" className="mkt-btn ghost lg">
          {t('marketing.cta.login')}
        </Link>
      </Hero>

      {/* Tier cards */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-pricing-tiers">
            {PRICING_TIERS.map((tier) => (
              <div key={tier.key} className={`mkt-pricing-tier ${tier.key}`}>
                <h3 className="mkt-pricing-tier-name">{t(tier.nameKey)}</h3>
                <TierPriceBlock price={tier.price} plans={plans} locale={locale} t={t} />
                <p className="mkt-pricing-tier-positioning">{t(tier.positioningKey)}</p>
                <Link to={tier.cta.to} className="mkt-btn primary">
                  {t(tier.cta.labelKey)}
                </Link>
              </div>
            ))}
          </div>
          <p className="mkt-pricing-note">
            {t('pricing.note')}
          </p>
        </div>
      </section>

      {/* Feature comparison */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <h2 className="mkt-h2">{t('pricing.comparison.heading')}</h2>
            <p className="mkt-section-lede">
              {t('pricing.comparison.lede')}
            </p>
          </div>
          <table className="mkt-pricing-table" aria-label={t('pricing.comparison.aria')}>
            <thead>
              <tr className="mkt-pricing-row mkt-pricing-row-head">
                <th scope="col">{t('pricing.comparison.column.feature')}</th>
                <th scope="col">{t('pricing.comparison.column.free')}</th>
                <th scope="col">{t('pricing.comparison.column.pro')}</th>
              </tr>
            </thead>
            <tbody>
              {PRICING_FEATURES.map((row) => (
                <tr key={row.labelKey} className="mkt-pricing-row">
                  <th scope="row">
                    <span className="mkt-pricing-feature-label">{t(row.labelKey)}</span>
                    {row.detailKey && (
                      <span className="mkt-pricing-feature-detail">{t(row.detailKey)}</span>
                    )}
                  </th>
                  <td>{renderCell(row.free, t)}</td>
                  <td>{renderCell(row.pro, t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mkt-cta-strip">
        <div className="mkt-container-narrow">
          <h2 className="mkt-h2">{t('pricing.cta.heading')}</h2>
          <p className="mkt-section-lede">
            {t('pricing.cta.lede')}
          </p>
          <div className="mkt-cta-row">
            <Link to="/register" className="mkt-btn primary lg">
              {t('marketing.cta.signup-free')}
            </Link>
            <Link to="/dashboard" className="mkt-btn ghost lg">
              {t('marketing.cta.login')}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
